import PropTypes from 'prop-types';
import { Box, Stack, TextField, Button, InputAdornment, IconButton, Tooltip, CircularProgress } from '@mui/material';
import { FilterAltOutlined, Search, CloseOutlined, FileDownloadOutlined, RefreshOutlined } from '@mui/icons-material';

const TransactionHistoryToolbar = ({
    fromDate,
    toDate,
    onFromDateChange,
    onToDateChange,
    onApplyFilter,
    onResetFilter,
    searchTerm,
    onSearchChange,
    onExport,
    isExporting = false,
    exportDisabled = false,
    onRefresh,
    loading = false
}) => {
    return (
        <Box sx={{ p: 2.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#fff' }}>
            <Stack
                direction={{ xs: 'column', lg: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', lg: 'center' }}
                justifyContent="space-between"
            >
                {/* Date Inputs & Filter/Reset Buttons */}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }} flexWrap="wrap">
                    <TextField
                        label="From Date"
                        type="date"
                        size="small"
                        value={fromDate}
                        onChange={(e) => onFromDateChange(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ width: { xs: '100%', sm: 165 }, bgcolor: '#f8fafc', borderRadius: 2 }}
                    />

                    <TextField
                        label="To Date"
                        type="date"
                        size="small"
                        value={toDate}
                        onChange={(e) => onToDateChange(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ width: { xs: '100%', sm: 165 }, bgcolor: '#f8fafc', borderRadius: 2 }}
                    />

                    <Button
                        variant="contained"
                        startIcon={<FilterAltOutlined />}
                        onClick={onApplyFilter}
                        disabled={loading}
                        sx={{
                            bgcolor: '#1e40af',
                            textTransform: 'none',
                            fontWeight: 700,
                            borderRadius: 2,
                            px: 2.5,
                            '&:hover': { bgcolor: '#1d4ed8' }
                        }}
                    >
                        Filter
                    </Button>

                    <Button
                        variant="outlined"
                        onClick={onResetFilter}
                        disabled={loading}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: 2,
                            borderColor: 'divider',
                            color: 'text.secondary',
                            '&:hover': { bgcolor: '#f8fafc' }
                        }}
                    >
                        Reset
                    </Button>
                </Stack>

                {/* Search & Export/Refresh Action Buttons */}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
                    <TextField
                        placeholder="Search order ID, email, RRN..."
                        size="small"
                        value={searchTerm}
                        onChange={(e) => onSearchChange(e.target.value)}
                        sx={{
                            width: { xs: '100%', sm: 280 },
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                bgcolor: '#f8fafc'
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search sx={{ color: 'text.disabled', fontSize: 20 }} />
                                </InputAdornment>
                            ),
                            endAdornment: searchTerm ? (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => onSearchChange('')}>
                                        <CloseOutlined sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </InputAdornment>
                            ) : null
                        }}
                    />

                    <Tooltip title="Export to Excel">
                        <span>
                            <Button
                                variant="outlined"
                                startIcon={
                                    isExporting ? (
                                        <CircularProgress size={16} color="inherit" />
                                    ) : (
                                        <FileDownloadOutlined sx={{ fontSize: '18px !important' }} />
                                    )
                                }
                                onClick={onExport}
                                disabled={isExporting || exportDisabled}
                                sx={{
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    borderColor: 'divider',
                                    color: 'text.primary',
                                    bgcolor: '#fff',
                                    '&:hover': { bgcolor: '#f8fafc', borderColor: 'primary.main' }
                                }}
                            >
                                {isExporting ? 'Exporting...' : 'Export'}
                            </Button>
                        </span>
                    </Tooltip>

                    <Tooltip title="Refresh Transactions">
                        <span>
                            <IconButton
                                size="small"
                                onClick={onRefresh}
                                disabled={loading}
                                sx={{
                                    p: 1,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                    bgcolor: '#fff',
                                    '&:hover': { bgcolor: '#f8fafc' }
                                }}
                            >
                                <RefreshOutlined fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>
            </Stack>
        </Box>
    );
};

TransactionHistoryToolbar.propTypes = {
    fromDate: PropTypes.string.isRequired,
    toDate: PropTypes.string.isRequired,
    onFromDateChange: PropTypes.func.isRequired,
    onToDateChange: PropTypes.func.isRequired,
    onApplyFilter: PropTypes.func.isRequired,
    onResetFilter: PropTypes.func.isRequired,
    searchTerm: PropTypes.string.isRequired,
    onSearchChange: PropTypes.func.isRequired,
    onExport: PropTypes.func.isRequired,
    isExporting: PropTypes.bool,
    exportDisabled: PropTypes.bool,
    onRefresh: PropTypes.func.isRequired,
    loading: PropTypes.bool
};

export default TransactionHistoryToolbar;
